import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | cancelled
  const polls = useRef(0);

  const isCancel = location.pathname.includes("cancel");

  useEffect(() => {
    if (isCancel) { setState("cancelled"); return; }
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }

    let timer;
    const poll = async () => {
      if (polls.current >= 10) { setState("failed"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); await refreshUser(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* retry */ }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-background grid place-items-center px-5">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-10 text-center rise" data-testid="payment-return">
        <Logo size={36} className="mx-auto text-foreground mb-6" />
        {state === "checking" && (<><Loader2 size={40} className="mx-auto mb-4 animate-spin text-muted-foreground" /><h1 className="font-display text-xl font-bold">Confirming payment…</h1><p className="text-sm text-muted-foreground mt-2">Hang tight while we activate your membership.</p></>)}
        {state === "success" && (<><CheckCircle2 size={44} className="mx-auto mb-4 text-emerald-500" /><h1 className="font-display text-2xl font-bold">Membership active!</h1><p className="text-sm text-muted-foreground mt-2 mb-6">You're all set. Make sure your identity is verified, then start connecting.</p><div className="flex flex-col gap-2"><Link to="/search" data-testid="return-search" className="h-11 rounded-xl bg-foreground text-background text-sm font-medium grid place-items-center hover:opacity-90 transition-opacity">Find Wi-Fi</Link><Link to="/verify" className="h-11 rounded-xl border border-border text-sm grid place-items-center hover:bg-accent">Verify identity</Link></div></>)}
        {state === "cancelled" && (<><XCircle size={44} className="mx-auto mb-4 text-muted-foreground" /><h1 className="font-display text-2xl font-bold">Checkout cancelled</h1><p className="text-sm text-muted-foreground mt-2 mb-6">No charge was made. You can pick a plan whenever you're ready.</p><button onClick={() => navigate("/plans")} className="h-11 px-6 rounded-xl bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity">Back to plans</button></>)}
        {state === "failed" && (<><XCircle size={44} className="mx-auto mb-4 text-red-500" /><h1 className="font-display text-2xl font-bold">Something went wrong</h1><p className="text-sm text-muted-foreground mt-2 mb-6">We couldn't confirm your payment. Please try again.</p><button onClick={() => navigate("/plans")} className="h-11 px-6 rounded-xl bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity">Back to plans</button></>)}
      </div>
    </div>
  );
}
